import { ConsultCTA } from "../components/ConsultCTA";
import { Icon } from "../components/Icon";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const STRATEGIES = [
    {
        icon: "Lightbulb",
        title: "Creating Communication Opportunities",
        description:
            "Pause and wait during everyday routines — meals, bath time, play — so your child gets both a reason and the time to communicate.",
    },
    {
        icon: "HandHeart",
        title: "Encouraging Expression of Needs",
        description:
            "Offer choices and gently encourage your child to express wants, needs, and feelings — with words, sounds, or gestures.",
    },
    {
        icon: "Sparkles",
        title: "Expanding Vocabulary",
        description:
            "Name objects, actions, and feelings as they happen. Simple, repeated words during play build meaningful vocabulary.",
    },
    {
        icon: "BookOpen",
        title: "Reading & Storytelling",
        description:
            "Share picture books and simple stories daily. Point, name, ask, and let your child turn pages and join in.",
    },
    {
        icon: "HelpCircle",
        title: "Asking & Answering Questions",
        description:
            "Start with simple choices — 'Do you want the ball or the car?' — and celebrate every attempt at an answer.",
    },
    {
        icon: "ListChecks",
        title: "Following Instructions",
        description:
            "Begin with short, one-step instructions during routines, and build up gradually as your child succeeds.",
    },
    {
        icon: "Repeat",
        title: "Turn-Taking",
        description:
            "Play simple back-and-forth games — rolling a ball, stacking blocks — to build the rhythm of conversation.",
    },
    {
        icon: "MessagesSquare",
        title: "Everyday Conversation",
        description:
            "Talk through your day together. Describe what you are doing, seeing, and feeling in short, clear sentences.",
    },
    {
        icon: "Home",
        title: "Home Practice",
        description:
            "Practice the activities shared in therapy sessions little and often — a few minutes daily matters more than long, rare sessions.",
    },
    {
        icon: "CalendarCheck",
        title: "Consistency",
        description:
            "Small, regular practice woven into daily life creates the strongest foundation for lasting communication progress.",
    },
];

const ParentGuidance = () => {
    usePageMeta(
        "Parent Guidance for Speech & Language Development | Speech Horizon, Patna",
        "Practical, encouraging guidance for parents: creating communication opportunities, expanding vocabulary, storytelling, turn-taking, and home practice strategies from Speech Horizon, Anishabad, Patna."
    );

    return (
        <div data-testid="parent-guidance-page">
            <PageHero
                eyebrow="Parent Guidance"
                lines={["Small Moments,"]}
                highlight="Big Progress"
                description="You don't need special equipment or training to support your child's communication. The everyday moments you already share are the most powerful opportunities of all."
            />

            <section className="bg-peach-soft/60 py-14" data-testid="guidance-statement-section">
                <div className="mx-auto max-w-4xl px-6 md:px-8">
                    <Reveal>
                        <div className="rounded-[2rem] border-l-4 border-peach-deep bg-white p-8 text-center shadow-lg shadow-peach/10">
                            <p className="font-jakarta text-xl font-extrabold leading-snug text-navy md:text-3xl">
                                Small Daily Practice + Consistent Support ={" "}
                                <span className="text-teal">Better Communication Opportunities</span>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="guidance-strategies-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="At Home"
                        title="Practical Strategies for Everyday Life"
                        description="Based on your child's therapy goals, these are the kinds of simple, practical ideas parents may be guided to use at home."
                    />
                    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {STRATEGIES.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.05}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:border-teal/25 hover:shadow-xl hover:shadow-teal/10">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal-soft text-teal-dark transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                                        <Icon name={item.icon} size={21} strokeWidth={1.9} />
                                    </span>
                                    <h3 className="mt-5 font-jakarta text-base font-bold text-navy md:text-lg">
                                        {item.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{item.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="guidance-encouragement-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <div>
                        <SectionHeading
                            align="left"
                            eyebrow="A Note for Parents"
                            title="You're Doing Better Than You Think"
                        />
                        <Reveal delay={0.15}>
                            <div className="mt-6 space-y-5 text-base leading-relaxed text-ink/70 md:text-lg">
                                <p>
                                    Parents play an important role in the speech therapy process. Everyday
                                    activities can provide valuable opportunities for children to practice
                                    communication skills at home.
                                </p>
                                <p>
                                    You don't need to be perfect — you just need to be present. Progress is built
                                    from small, warm, everyday interactions, repeated with love and consistency.
                                </p>
                                <p>
                                    Based on the therapy goals, parents may be provided with practical home
                                    activities and ideas to encourage communication development in everyday
                                    situations.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal className="relative">
                        <div className="absolute -right-5 -top-5 h-full w-full rotate-[2deg] rounded-[2.5rem] bg-teal-soft" />
                        <img
                            src={IMAGES.kids}
                            alt="A child exploring the world through play with a toy camera"
                            className="relative h-[420px] w-full rounded-[2.5rem] object-cover shadow-xl shadow-navy/10 md:h-[500px]"
                        />
                    </Reveal>
                </div>
            </section>

            <ConsultCTA
                title="Book a Consultation"
                text="Would you like guidance tailored to your child's communication needs? Book a consultation and take home practical strategies you can start using right away."
            />
        </div>
    );
};

export default ParentGuidance;
